import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { isSupabaseConfigured } from '../lib/env';

export interface AuthResult {
  error: string | null;
}

export interface AuthContextValue {
  session: Session | null;
  user: User | null;
  /** True until the initial session lookup finishes. Gate routing on this. */
  initialising: boolean;
  signUp: (email: string, password: string, displayName: string) => Promise<AuthResult>;
  signIn: (email: string, password: string) => Promise<AuthResult>;
  signOut: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Turns Supabase's error messages into something a user can act on. The raw
 * strings are terse and a couple of them are actively misleading if email
 * confirmation hasn't been switched off yet.
 */
function describeAuthError(message: string): string {
  const m = message.toLowerCase();

  if (m.includes('invalid login credentials')) {
    return 'That email and password combination is not right.';
  }
  if (m.includes('email not confirmed')) {
    // The single most likely misconfiguration at this stage of the project.
    return (
      'This account exists but Supabase is still waiting for an email ' +
      'confirmation. Turn confirmation off: Supabase dashboard → Authentication ' +
      '→ Sign In / Providers → Email → uncheck "Confirm email". See SETUP.md.'
    );
  }
  if (m.includes('user already registered') || m.includes('already been registered')) {
    return 'There is already an account with that email. Try signing in instead.';
  }
  if (m.includes('password should be at least')) {
    return 'Password is too short — use at least 6 characters.';
  }
  if (m.includes('failed to fetch') || m.includes('networkerror') || m.includes('load failed')) {
    return (
      'Could not reach Supabase. Check VITE_SUPABASE_URL in .env.local, and ' +
      'that you restarted the dev server after editing it.'
    );
  }
  if (m.includes('signups not allowed')) {
    return 'Sign-ups are disabled for this Supabase project. Enable them under Authentication → Sign In / Providers.';
  }
  return message;
}

const NOT_CONFIGURED: AuthResult = {
  error:
    'Supabase is not configured yet. Fill in .env.local and restart the dev server — see SETUP.md.',
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [initialising, setInitialising] = useState(true);

  useEffect(() => {
    // Without credentials there is no session to look up, and calling getSession
    // against a fake host just stalls. Settle immediately instead.
    if (!isSupabaseConfigured) {
      setInitialising(false);
      return;
    }

    let cancelled = false;

    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (!cancelled) setSession(data.session);
      })
      .finally(() => {
        if (!cancelled) setInitialising(false);
      });

    // Fires on sign in, sign out, and every background token refresh.
    const { data: subscription } = supabase.auth.onAuthStateChange((_event, next) => {
      if (!cancelled) setSession(next);
    });

    return () => {
      cancelled = true;
      subscription.subscription.unsubscribe();
    };
  }, []);

  const signUp = useCallback<AuthContextValue['signUp']>(async (email, password, displayName) => {
    if (!isSupabaseConfigured) return NOT_CONFIGURED;

    const { error } = await supabase.auth.signUp({
      email: email.trim(),
      password,
      options: {
        // The handle_new_user() trigger in db/supabase-schema.sql reads
        // raw_user_meta_data->>'display_name' to seed the profiles row. The key
        // has to be exactly this or every new profile falls back to the email
        // local-part.
        data: { display_name: displayName.trim() },
      },
    });

    if (error) return { error: describeAuthError(error.message) };

    // With "Confirm email" off, signUp returns an active session and
    // onAuthStateChange has already pushed it into state.
    return { error: null };
  }, []);

  const signIn = useCallback<AuthContextValue['signIn']>(async (email, password) => {
    if (!isSupabaseConfigured) return NOT_CONFIGURED;

    const { error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });

    return { error: error ? describeAuthError(error.message) : null };
  }, []);

  const signOut = useCallback(async () => {
    if (!isSupabaseConfigured) return;
    await supabase.auth.signOut();
    setSession(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user: session?.user ?? null,
      initialising,
      signUp,
      signIn,
      signOut,
    }),
    [session, initialising, signUp, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
